<template>
  <model-form
    ref="modelForm"
    :model="inventoryItemBulkAdjustment"
    max-content-width="100%"
    :model-label="adjustmentType ? `Inventory Item ${$_.startCase(adjustmentType)}` : null"
    @update:model-value="value => shown = value"
  >
    <div class="row q-col-gutter-md">
      <q-select
        v-model="inventoryItemBulkAdjustment.adjustmentType"
        :options="inventoryItemBulkAdjustment.fieldOptions('adjustmentType')"
        :readonly="!$_.isNil(validatedAdjustmentType) || inventoryItemBulkAdjustment.readonly"
        :rules="[val => !!val || 'Field is required']"
        bottom-slots
        :class="
          inventoryItemBulkAdjustment.isTransferReceipt
            ? 'col-3'
            : inventoryItemBulkAdjustment.isTransfer
              ? 'col-2'
              : 'col-4'
        "
        emit-value
        label="Type of Adjustment"
        map-options
        @update:model-value="validateAdjustmentType"
      />
      <q-checkbox
        v-if="inventoryItemBulkAdjustment.isTransfer"
        v-model="inventoryItemBulkAdjustment.siteTransfer"
        :disable="inventoryItemBulkAdjustment.readonly"
        label="Transfer Between Sites"
        class="col-1 q-pt-none"
        bottom-slots
      />
      <q-select
        v-model="inventoryItemBulkAdjustment.locationOrRegisterStation"
        :display-value="locationOrRegisterStationDisplayValue"
        :options="locationOrRegisterStationOptions"
        option-label="inventoryStrategyEntityDisplayName"
        :readonly="inventoryItemBulkAdjustment.readonly || $register"
        :rules="[inventoryItemBulkAdjustment.hasLocationOrRegisterStation || 'Field is required']"
        bottom-slots
        :class="inventoryItemBulkAdjustment.isTransfer || inventoryItemBulkAdjustment.isTransferReceipt ? 'col-3' : 'col-4'"
        label="Location"
        clearable
      />
      <q-select
        v-if="inventoryItemBulkAdjustment.isTransfer || inventoryItemBulkAdjustment.isTransferReceipt"
        v-model="inventoryItemBulkAdjustment.transferLocationOrRegisterStation"
        :display-value="transferLocationOrRegisterStationDisplayValue"
        :options="transferLocationOrRegisterStationOptions"
        option-label="inventoryStrategyEntityDisplayName"
        :readonly="inventoryItemBulkAdjustment.readonly"
        :rules="[inventoryItemBulkAdjustment.hasTransferLocationOrRegisterStation || 'Field is required']"
        bottom-slots
        class="col-3"
        :label="inventoryItemBulkAdjustment.isTransferReceipt ? 'From Location' : 'To Location'"
        clearable
      />
      <q-input
        v-if="inventoryItemBulkAdjustment.committedAt"
        :class="inventoryItemBulkAdjustment.isTransfer || inventoryItemBulkAdjustment.isTransferReceipt ? 'col-3' : 'col-4'"
        :model-value="inventoryItemBulkAdjustment.committedAtSiteTime && date.formatDate(inventoryItemBulkAdjustment.committedAtSiteTime, 'YYYY MMM D, h:mm a')"
        label="Committed At"
        readonly
      />
      <q-input
        v-else
        :class="inventoryItemBulkAdjustment.isTransfer || inventoryItemBulkAdjustment.isTransferReceipt ? 'col-3' : 'col-4'"
        :model-value="inventoryItemBulkAdjustment.effectiveAtSiteTime && date.formatDate(inventoryItemBulkAdjustment.effectiveAtSiteTime, 'YYYY MMM D, h:mm a')"
        label="Effective At"
        :readonly="$register"
      >
        <template
          v-if="!$register"
          #prepend
        >
          <q-icon
            name="event"
            class="cursor-pointer"
          >
            <q-popup-proxy
              transition-show="scale"
              transition-hide="scale"
            >
              <q-date
                :model-value="inventoryItemBulkAdjustment.effectiveAtSiteTime && date.formatDate(inventoryItemBulkAdjustment.effectiveAtSiteTime, 'YYYY-MM-DDTHH:mm:ssZ')"
                flat
                mask="YYYY-MM-DDTHH:mm:ssZ"
                :options="effectiveDateOptionsFn"
                @update:model-value="value => inventoryItemBulkAdjustment.setEffectiveAt(new Date(value))"
              />
            </q-popup-proxy>
          </q-icon>
        </template>
        <template
          v-if="!$register"
          #append
        >
          <q-icon
            name="access_time"
            class="cursor-pointer"
          >
            <q-popup-proxy
              transition-show="scale"
              transition-hide="scale"
            >
              <q-time
                :model-value="inventoryItemBulkAdjustment.effectiveAtSiteTime && date.formatDate(inventoryItemBulkAdjustment.effectiveAtSiteTime, 'YYYY-MM-DDTHH:mm:ssZ')"
                flat
                mask="YYYY-MM-DDTHH:mm:ssZ"
                @update:model-value="value => inventoryItemBulkAdjustment.setEffectiveAt(new Date(date.formatDate(inventoryItemBulkAdjustment.effectiveAtSiteTime, 'YYYY-MM-DD') + date.formatDate(value, 'THH:mm:ss') + date.formatDate(inventoryItemBulkAdjustment.effectiveAtSiteTime, 'Z')))"
              />
            </q-popup-proxy>
          </q-icon>
        </template>
      </q-input>
    </div>
    <div
      class="row q-col-gutter-md"
    >
      <q-select
        v-if="inventoryItemBulkAdjustment.isReceipt"
        v-model="inventoryItemBulkAdjustment.inventorySupplier"
        :display-value="inventorySupplierName"
        :options="inventorySupplierOptions"
        :readonly="inventoryItemBulkAdjustment.readonly"
        bottom-slots
        class="col-4"
        label="Supplier"
        option-label="name"
        clearable
        use-input
        @filter="inventorySupplierFilterFunction"
      />
      <q-input
        v-for="customTextField in customTextFields"
        :key="customTextField.field_key"
        v-model="inventoryItemBulkAdjustment.customAttributes[customTextField.field_key]"
        class="col-4"
        :label="customTextField.field_name"
        :rules="[val => !!val || !customTextField.required || 'Field is required']"
      />
    </div>
    <div
      v-if="inventoryItemBulkAdjustment.showAdjustmentsTable"
      class="row justify-between q-pt-lg items-end"
    >
      <div class="row col-6 q-col-gutter-md q-mr-md">
        <q-select
          v-model="sortBy"
          class="col-6"
          label="Sort By"
          :options="[{ label: 'Inventory Item', value: 'inventoryItem.name' }, { label: 'Item Class', value: 'inventoryItem.itemClass.name' }]"
          map-options
          emit-value
          bottom-slots
          dense
        />
        <q-select
          v-if="inventoryItemBulkAdjustment.isReceipt || inventoryItemBulkAdjustment.isStocktake"
          v-model="itemClassId"
          class="col-6"
          label="Item Class Filter"
          :options="$_.sortBy(itemClasses.data, 'name')"
          option-label="name"
          option-value="id"
          emit-value
          :display-value="itemClassName || 'All'"
          bottom-slots
          clearable
          dense
        />
      </div>
      <div class="q-pb-md">
        <q-checkbox
          v-if="!inventoryItemBulkAdjustment.committedAt && !$register"
          v-model="inventoryItemBulkAdjustment.action"
          label="Commit Bulk Adjustment"
          :true-value="'commit'"
          :false-value="null"
          left-label
        />
        <q-btn
          v-if="inventoryItemBulkAdjustment.isStocktake"
          label="Generate Adjustments"
          :disable="inventoryItems.loading || inventoryItemBalancesByLocationAndRegisterStation.loading"
          @click.stop="generateAdjustments"
        />
        <q-btn
          v-if="inventoryItemBulkAdjustment"
          icon="fas fa-file-pdf"
          :loading="inventoryItems.loading || pdfProcessing"
          @click.stop="downloadPdf"
        >
          <q-tooltip>
            Download PDF
          </q-tooltip>
        </q-btn>
        <q-btn
          v-if="inventoryItemBulkAdjustment.isStocktake && !$register"
          icon="autorenew"
          :loading="inventoryItemBalancesByLocationAndRegisterStation.loading"
          @click.stop="refreshInventoryItemBalances"
        >
          <q-tooltip>
            Refresh Expected Qty
          </q-tooltip>
        </q-btn>
      </div>
    </div>

    <model-table
      v-if="inventoryItemBulkAdjustment.showAdjustmentsTable"
      title="Inventory Item Adjustments"
      :submodel="true"
      :edit-mode="true"
      :collection="inventoryItemBulkAdjustment.inventoryItemAdjustments"
      :loading="inventoryItems.loading"
      :filter-by-data-array="inventoryItemAdjustments"
      :columns="columns"
      :show-filter="false"
      :show-edit="false"
      :show-add="allowAddAdjustment"
      :add-label="addAdjustmentLabel"
      :constructor-data="inventoryItemBulkAdjustment.constructorData"
      :header-buttons="inventoryItemBulkAdjustment.isWaste && !inventoryItemBulkAdjustment.committedAt ? [{
        label: 'Add From Recipe',
        handler: () => showRecipeWastageAdjustmentDialog = true
      }] : []"
      :actions="actions"
    />
    <div
      v-if="inventoryItemBulkAdjustment.showAdjustmentsTable && inventoryItemBulkAdjustment.isReceipt"
      class="row justify-end q-mt-xl"
    >
      <div
        class="col-3 column q-px-none"
      >
        <q-item
          v-for="(itemClassCost, index) in itemClassCostSummary"
          :key="`item-class-cost-${index}`"
          class="row q-col-gutter-md q-mx-none"
        >
          <q-item-section class="col-6">
            {{ itemClassCost.itemClass }}
          </q-item-section>
          <q-item-section class="col-6 text-right">
            {{ $i18n.n(itemClassCost.amount, 'currency') }}
          </q-item-section>
        </q-item>
        <q-item
          class="row q-col-gutter-md q-mx-none"
        >
          <q-item-section
            class="col-6"
          >
            <span class="q-my-sm">Subtotal</span>
          </q-item-section>
          <q-item-section class="col-6 text-right">
            {{ $i18n.n(costSubtotal, 'currency') }}
          </q-item-section>
        </q-item>
        <q-item
          v-for="customCostField in customCostFields"
          :key="customCostField.field_key"
          class="row q-col-gutter-md q-ma-none q-pr-xs"
        >
          <q-item-section class="col-6">
            {{ customCostField.field_name }}
          </q-item-section>
          <q-input
            v-model.number="inventoryItemBulkAdjustment.customAttributes[customCostField.field_key]"
            :hide-bottom-space="true"
            :rules="[val => (val !== '' && val !== null && val !== undefined && val >= 0) || !customCostField.required || 'Field is required']"
            class="col-6"
            input-class="text-right"
            type="number"
            step="0.01"
            :prefix="currentCircuit.localeCurrencySymbol"
            dense
          />
        </q-item>
        <q-item
          class="row q-col-gutter-md q-mx-none"
        >
          <q-item-section class="col-6">
            <span class="q-my-sm">Total</span>
          </q-item-section>
          <q-item-section class="col-6 text-right">
            {{ costTotal }}
          </q-item-section>
        </q-item>
      </div>
    </div>

    <q-dialog
      v-model="showRecipeWastageAdjustmentDialog"
    >
      <q-card
        style="width: 400px;"
      >
        <q-card-section class="row items-center q-mx-xs">
          <div class="text-h6">
            Add From Recipe
          </div>
          <q-space />
          <q-btn
            v-close-popup
            icon="close"
            flat
            round
            dense
          />
        </q-card-section>
        <q-card-section class="row q-col-gutter-md">
          <q-select
            v-model="inventoryRecipeToWaste"
            class="col-8"
            label="Inventory Recipe"
            :options="inventoryRecipeOptions"
            map-options
            emit-value
            option-label="name"
            bottom-slots
            clearable
            use-input
            @filter="inventoryRecipeFilterFunction"
          />
          <q-input
            v-model.number="inventoryRecipeToWasteQuantity"
            :rules="[val => !!val || 'Field is required']"
            bottom-slots
            class="col-4"
            label="Quantity"
            type="number"
          />
        </q-card-section>
        <q-card-actions class="row justify-end q-ma-sm">
          <q-btn
            label="Add Adjustments"
            color="primary"
            :disable="!inventoryRecipeToWaste || !inventoryRecipeToWasteQuantity"
            @click="generateAdjustmentsFromRecipe"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <section
      class="pdf-source-container"
    >
      <div
        id="inventory-item-adjustments-preview"
      >
        <section
          style="width: 100%;"
        >
          <div class="row q-col-gutter-md q-mb-md">
            <div>
              <strong>Type of Adjustment: </strong>
              <span>{{ inventoryItemBulkAdjustment.adjustmentType }}</span>
            </div>

            <div>
              <strong>Location: </strong>
              <span>{{ locationOrRegisterStationDisplayValue }}</span>
            </div>

            <div
              v-if="inventoryItemBulkAdjustment.isTransfer || inventoryItemBulkAdjustment.isTransferReceipt"
            >
              <strong v-if="inventoryItemBulkAdjustment.isTransferReceipt">From Location: </strong>
              <strong v-else>To Location: </strong>
              <span>{{ transferLocationOrRegisterStationDisplayValue }}</span>
            </div>

            <div
              v-if="inventoryItemBulkAdjustment.committedAt"
            >
              <strong>Committed At: </strong>
              <span>{{ inventoryItemBulkAdjustment.committedAtSiteTime && date.formatDate(inventoryItemBulkAdjustment.committedAtSiteTime, 'YYYY MMM D, h:mm a') }}</span>
            </div>
            <div
              v-else
            >
              <strong>Effective At: </strong>
              <span>{{ inventoryItemBulkAdjustment.effectiveAtSiteTime && date.formatDate(inventoryItemBulkAdjustment.effectiveAtSiteTime, 'YYYY MMM D, h:mm a') }}</span>
            </div>

            <div
              v-if="inventoryItemBulkAdjustment.showAdjustmentsTable"
            >
              <div
                v-if="inventoryItemBulkAdjustment.isReceipt || inventoryItemBulkAdjustment.isStocktake"
              >
                <strong>Item Class Filter: </strong>
                <span>{{ itemClassName || 'All' }}</span>
              </div>
            </div>
          </div>

          <model-table
            :submodel="true"
            :collection="inventoryItemBulkAdjustment.inventoryItemAdjustments"
            :filter-by-data-array="inventoryItemAdjustments"
            :columns="columnsForPrint"
            dense
            dense-table
            :show-filter="false"
            :show-edit="false"
            :show-add="false"
          />
        </section>
      </div>
    </section>
  </model-form>
</template>

<script>
import html2pdf from 'html2pdf.js'
import { date } from 'quasar'
import { ADJUSTMENT_TYPE_OPTIONS } from 'src/models/inventory-item/InventoryItemAdjustment'
import ModelForm from 'src/modules/admin/components/ModelForm/ModelForm'
import ModelTable from 'src/modules/admin/components/ModelTable'
import InventoryItemAdjustmentUnitSelectEditorCell from 'src/modules/admin/inventory-item/components/InventoryItemAdjustmentUnitSelectEditorCell'

export default {
  name: 'InventoryItemBulkAdjustmentForm',
  components: {
    ModelForm,
    ModelTable
  },
  context: ['currentUser'],
  configuration: ['currentCircuit', 'currentSite', 'locations', 'registerStations', 'itemClasses'],
  props: {
    adjustmentType: {
      type: String,
      default: null
    },
    isModal: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      date,
      inventoryItemsFilterString: null,
      inventoryRecipesFilterString: null,
      inventorySuppliersFilterString: null,
      itemClassId: null,
      sortBy: 'inventoryItem.itemClass.name',
      pdfLoading: false,
      showRecipeWastageAdjustmentDialog: false,
      inventoryRecipeToWaste: null,
      inventoryRecipeToWasteQuantity: 1,
      shown: false,
      pdfProcessing: false
    }
  },
  computed: {
    customTextFields () {
      if (!this.inventoryItemBulkAdjustment.adjustmentType) return []

      return _.filter(
        this.currentSite.activeInventoryStrategy.customFields[this.inventoryItemBulkAdjustment.adjustmentType],
        { field_type: 'text' }
      )
    },
    customCostFields () {
      if (!this.inventoryItemBulkAdjustment.adjustmentType) return []

      return _.filter(
        this.currentSite.activeInventoryStrategy.customFields[this.inventoryItemBulkAdjustment.adjustmentType],
        { field_type: 'cost' }
      )
    },
    validatedAdjustmentType () {
      return _.find(ADJUSTMENT_TYPE_OPTIONS.BULK, option => {
        return option === _.startCase(this.adjustmentType)
      })
    },
    locationOrRegisterStationDisplayValue () {
      return (
        this.inventoryItemBulkAdjustment.location ||
        this.inventoryItemBulkAdjustment.registerStation
      )?.inventoryStrategyEntityDisplayName
    },
    transferLocationOrRegisterStationDisplayValue () {
      return (
        _.find(this.currentCircuit.locations.data, ({ id }) => id === this.inventoryItemBulkAdjustment.transferLocationId) ||
        _.find(this.registerStations.data, ({ id }) => id === this.inventoryItemBulkAdjustment.transferRegisterStationId)
      )?.inventoryStrategyEntityDisplayName
    },
    allowAddAdjustment () {
      // DON'T ALLOW NEW ADJUSTMENTS TO BE CREATED IF THE BULK ADJUSTMENT IS
      // LINKED TO AN INVALID STOCK LOCATION.
      if (
        this.inventoryItemBulkAdjustment.location &&
        !this.currentSite.activeInventoryStrategy?.allowInventoryAtLocation(this.inventoryItemBulkAdjustment.location.id)
      ) return false

      if (
        this.inventoryItemBulkAdjustment.registerStation &&
        !this.currentSite.activeInventoryStrategy?.allowInventoryAtRegisterStation(this.inventoryItemBulkAdjustment.registerStation.id)
      ) return false

      return !this.inventoryItemBulkAdjustment.committedAt && !this.inventoryItemBulkAdjustment.isTransferReceipt
    },
    inventorySupplierName () {
      return this.inventoryItemBulkAdjustment.inventorySupplier?.name
    },
    itemClassName () {
      const itemClass = _.find(this.itemClasses.data, itemClass => {
        return itemClass.id === Number(this.itemClassId)
      })
      return itemClass?.name
    },
    inventoryItemAdjustments () {
      const adjustments = this.itemClassId
        ? _.filter(this.inventoryItemBulkAdjustment.inventoryItemAdjustments.data, inventoryItemAdjustment => {
          return !inventoryItemAdjustment.inventoryItem || inventoryItemAdjustment.inventoryItem.itemClass?.id === Number(this.itemClassId)
        })
        : this.inventoryItemBulkAdjustment.inventoryItemAdjustments.data

      return _.sortBy(adjustments, this.sortBy)
    },
    inventoryItemIdsWithInventoryItemAdjustments () {
      return _.map(this.inventoryItemBulkAdjustment.inventoryItemAdjustments.data, 'inventoryItemId')
    },
    inventoryItemOptions () {
      const activeInventoryItems = _.filter(this.inventoryItems.data, 'isUnarchived')

      const availableInventoryItems = this.inventoryItemBulkAdjustment.isStocktake
        // Only allow one stocktake adjustment per inventory item.
        ? _.filter(activeInventoryItems, inventoryItem => !_.includes(this.inventoryItemIdsWithInventoryItemAdjustments, inventoryItem.id))
        : activeInventoryItems

      const supplierFilteredInventoryItems = this.inventoryItemBulkAdjustment.inventorySupplier
        ? _.filter(availableInventoryItems, inventoryItem => {
          const suppliersIds = inventoryItem.inventorySupplierIds
          return suppliersIds.includes(this.inventoryItemBulkAdjustment.inventorySupplier.id)
        })
        : availableInventoryItems

      const ItemClassFilteredInventoryItems = this.itemClassId
        ? _.filter(supplierFilteredInventoryItems, inventoryItem => {
          return inventoryItem.itemClass?.id === Number(this.itemClassId)
        })
        : supplierFilteredInventoryItems

      if (!this.inventoryItemsFilterString) {
        return ItemClassFilteredInventoryItems
      }

      const keywords = this.inventoryItemsFilterString.toLowerCase().split(' ')

      return _.filter(ItemClassFilteredInventoryItems, inventoryItem => {
        const inventoryItemName = inventoryItem.name.toLowerCase()
        return _.some(keywords, keyword => inventoryItemName.includes(keyword))
      })
    },
    inventoryRecipeOptions () {
      if (!this.inventoryRecipesFilterString) {
        return this.inventoryRecipes.data
      }

      const keywords = this.inventoryRecipesFilterString.toLowerCase().split(' ')

      return _.filter(this.inventoryRecipes.data, inventoryRecipe => {
        const inventoryRecipeName = inventoryRecipe.name.toLowerCase()
        return _.some(keywords, keyword => inventoryRecipeName.includes(keyword))
      })
    },

    inventorySupplierOptions () {
      if (!this.inventorySuppliersFilterString) {
        return this.inventorySuppliers.data
      }

      const keywords = this.inventorySuppliersFilterString.toLowerCase().split(' ')

      return _.filter(this.inventorySuppliers.data, inventorySupplier => {
        const inventorySupplierName = inventorySupplier.name.toLowerCase()
        return _.some(keywords, keyword => inventorySupplierName.includes(keyword))
      })
    },

    registerStationOptions () {
      return _.filter(this.registerStations.data, 'isUnarchived')
    },
    locationOrRegisterStationOptions () {
      const locationsAndRegisterStations = []

      if (this.inventoryItemBulkAdjustment.siteTransfer) {
        _.each(this.locations.data, location => {
          const isDefaultLocation = this.currentSite.activeInventoryStrategy.defaultInventoryLocationId === location.id

          if (isDefaultLocation) {
            locationsAndRegisterStations.push(location)
          }
        })
      } else {
        _.each(this.locations.data, location => {
          const allowAtLocation = !!this.currentSite.activeInventoryStrategy?.allowInventoryAtLocation(location.id)
          const isAlreadySelected = location.id === this.inventoryItemBulkAdjustment.transferLocationId

          if (allowAtLocation && !isAlreadySelected) {
            locationsAndRegisterStations.push(location)
          }
        })

        _.each(this.registerStationOptions, registerStation => {
          const allowAtRegisterStation = !!this.currentSite.activeInventoryStrategy?.allowInventoryAtRegisterStation(registerStation.id)
          const isAlreadySelected = registerStation.id === this.inventoryItemBulkAdjustment.transferRegisterStationId

          if (allowAtRegisterStation && !isAlreadySelected) {
            locationsAndRegisterStations.push(registerStation)
          }
        })
      }

      return locationsAndRegisterStations
    },
    transferLocationOrRegisterStationOptions () {
      const locationsAndRegisterStations = []

      if (this.inventoryItemBulkAdjustment.siteTransfer) {
        _.each(this.currentCircuit.locations.data, location => {
          const belongsToSiblingSite = location.locationGroup.site.id !== this.currentSite.id
          const isDefaultLocationForSiblingSite = location.locationGroup.site.activeInventoryStrategy?.defaultInventoryLocationId === location.id

          if (belongsToSiblingSite && isDefaultLocationForSiblingSite) {
            locationsAndRegisterStations.push(location)
          }
        })
      } else {
        _.each(this.locations.data, location => {
          const allowAtLocation = !!this.currentSite.activeInventoryStrategy?.allowInventoryAtLocation(location.id)
          const isAlreadySelected = location.id === this.inventoryItemBulkAdjustment.location?.id

          if (allowAtLocation && !isAlreadySelected) {
            locationsAndRegisterStations.push(location)
          }
        })

        _.each(this.registerStationOptions, registerStation => {
          const allowAtRegisterStation = this.currentSite.activeInventoryStrategy.allowInventoryAtRegisterStation(registerStation.id)
          const isAlreadySelected = registerStation.id === this.inventoryItemBulkAdjustment.registerStation?.id

          if (allowAtRegisterStation && !isAlreadySelected) {
            locationsAndRegisterStations.push(registerStation)
          }
        })
      }

      return locationsAndRegisterStations
    },

    columns () {
      return [
        {
          name: 'itemClass',
          field: adjustment => this.getItemClassName(adjustment),
          headerStyle: 'vertical-align: bottom; width: 15%',
          style: 'width: 15%'
        },
        {
          name: 'inventoryItem',
          field: adjustment => this.getInventoryItemName(adjustment),
          editor: {
            component: 'select',
            options: this.inventoryItemOptions,
            optionLabel: 'name',
            label: 'Inventory Item',
            rules: [val => !!val || 'Field is required'],
            filterFunction: this.inventoryItemFilterFunction
          },
          headerStyle: 'vertical-align: bottom; width: 20%',
          style: 'width: 20%',
          loading: this.inventoryItems.loading
        },
        {
          name: 'inventoryItemUnit',
          field: adjustment => this.getInventoryItemUnitDescription(adjustment),
          editor: {
            component: InventoryItemAdjustmentUnitSelectEditorCell
          },
          label: 'Unit',
          headerStyle: 'vertical-align: bottom; width: 20%',
          style: 'width: 20%',
          loading: this.inventoryItems.loading
        },
        {
          field: 'unitQuantity',
          editor: {
            component: 'input',
            label: 'Unit Qty',
            rules: this.inventoryItemBulkAdjustment.unitQuantityRules,
            type: 'number',
            step: '1',
            min: '0'
          },
          label: 'Unit Qty',
          headerStyle: 'vertical-align: bottom; width: 10%',
          style: 'width: 10%',
          loading: this.inventoryItems.loading
        },
        ...(this.inventoryItemBulkAdjustment.showAdjustmentReason
          ? [
              {
                field: 'adjustmentReason',
                editor: {
                  component: 'input',
                  label: this.inventoryItemBulkAdjustment.isMisc ? 'Reason' : 'Reason (Optional)',
                  rules: this.inventoryItemBulkAdjustment.adjustmentReasonRules
                },
                label: this.inventoryItemBulkAdjustment.isMisc ? 'Reason' : 'Reason (Optional)',
                headerStyle: 'vertical-align: bottom; width: 20%',
                style: 'width: 20%'
              }
            ]
          : []),
        ...(this.inventoryItemBulkAdjustment.isReceipt
          ? [
              {
                field: 'cost',
                editor: {
                  component: 'input',
                  label: 'Cost',
                  rules: [val => !!val || val >= 0 || 'Field is required'],
                  type: 'number',
                  min: '0',
                  step: '0.01',
                  prefix: this.currentCircuit.localeCurrencySymbol
                },
                label: 'Cost',
                component: 'money',
                headerStyle: 'vertical-align: bottom; width: 10%',
                style: 'width: 10%'
              }
            ]
          : []),
        ...(this.inventoryItemBulkAdjustment.isStocktake
          ? [
              {
                name: 'baseQuantity',
                field: adjustment => this.getInventoryItemTotalBaseQuantity(adjustment),
                label: 'Base Qty',
                headerStyle: 'vertical-align: bottom; width: 5%',
                style: 'width: 5%',
                loading: this.inventoryItems.loading,
                align: 'center'
              },
              ...(this.inventoryItemBulkAdjustment.isCommitted || this.$register
                ? []
                : [
                    {
                      name: 'expectedQuantity',
                      field: adjustment => this.getInventoryItemBalance(adjustment),
                      label: 'Expected',
                      headerStyle: 'vertical-align: bottom; width: 5%',
                      style: 'width: 5%',
                      loading: this.inventoryItemBalancesByLocationAndRegisterStation.loading,
                      align: 'center'
                    },
                    {
                      name: 'diffQuantity',
                      field: adjustment => this.getInventoryItemCountDiff(adjustment),
                      label: 'Diff',
                      headerStyle: 'vertical-align: bottom; width: 5%',
                      style: adjustment => `width: 5%; color: ${this.getInventoryItemCountDiff(adjustment) < 0 ? 'var(--q-negative)' : 'inherit'}`,
                      loading: this.inventoryItemBalancesByLocationAndRegisterStation.loading,
                      align: 'center'
                    }
                  ])
            ]
          : []),
        {
          name: 'actions',
          align: 'right'
        }
      ]
    },
    actions () {
      return this.inventoryItemBulkAdjustment.isTransferReceipt
        ? []
        : [
            {
              label: 'Copy',
              icon: 'content_copy',
              disabled: function (adjustment) {
                return !adjustment.inventoryItem || !adjustment.inventoryItemUnit || adjustment.isTransferReceipt
              },
              disabledTooltip: () => 'Unable to copy without an inventory item.',
              handler: this.copyInventoryItemAdjustment
            }
          ]
    },
    columnsForPrint () {
      return _.filter(this.columns, column => {
        return column.name !== 'action' && column.name !== 'actions'
      })
    },
    addAdjustmentLabel () {
      return `${this.inventoryItemBulkAdjustment.adjustmentType} Adjustment`
    },
    costSubtotal () {
      return _(this.inventoryItemBulkAdjustment.inventoryItemAdjustments.data).map(adjustment => Number(adjustment.cost || 0)).sum()
    },
    costTotal () {
      return this.costSubtotal + _(this.customCostFields)
        .map(customCostField => Number(this.inventoryItemBulkAdjustment.customAttributes[customCostField.field_key] || 0))
        .sum()
    },
    itemClassCostSummary () {
      return _(this.inventoryItemBulkAdjustment.inventoryItemAdjustments.data)
        .filter(adjustment => {
          const itemClassNameForAdjustment = this.getItemClassName(adjustment)
          if (this.itemClassName) {
            return !!itemClassNameForAdjustment && itemClassNameForAdjustment === this.itemClassName
          } else {
            return !!itemClassNameForAdjustment
          }
        })
        .groupBy(this.getItemClassName)
        .map((adjustments, itemClassName) => {
          return {
            itemClass: itemClassName,
            amount: _(adjustments).map(adjustment => Number(adjustment.cost || 0)).sum()
          }
        })
        .sortBy('itemClass')
        .value()
    }
  },
  watch: {
    itemClassId () {
      if (this.itemClassId) {
        this.filteredInventoryItemAdjustments = _.filter(this.inventoryItemBulkAdjustment.inventoryItemAdjustments.data, inventoryItemAdjustment => {
          return !inventoryItemAdjustment.inventoryItem || inventoryItemAdjustment.inventoryItem?.itemClass?.id === Number(this.itemClassId)
        })
        this.sortedInventoryItemAdjustments = null
      } else {
        this.filteredInventoryItemAdjustments = null
        this.sortedInventoryItemAdjustments = null
      }
    },
    'inventoryItemBulkAdjustment.id': {
      handler: function () {
        if (!this.inventoryItemBulkAdjustment.adjustmentType && this.validatedAdjustmentType) {
          this.inventoryItemBulkAdjustment.adjustmentType = this.validatedAdjustmentType
        }

        if (!this.inventoryItemBulkAdjustment.locationOrRegisterStation && this.locationOrRegisterStationOptions.length === 1) {
          this.inventoryItemBulkAdjustment.locationOrRegisterStation = this.locationOrRegisterStationOptions[0]

          if (this.inventoryItemBulkAdjustment.isTransfer) {
            this.inventoryItemBulkAdjustment.siteTransfer = true
          }
        }
      },
      immediate: true
    }
  },
  models: {
    inventoryItemBulkAdjustment: {
      inventorySupplier: true,
      inventoryItemAdjustments: {
        inventoryItem: {
          itemClass: true,
          inventoryItemUnits: true,
          inventorySupplierIds: true
        },
        inventoryItemUnit: true
      }
    },
    inventoryItems: {
      itemClass: true,
      inventoryItemUnits: true,
      inventorySupplierIds: true,
      variables () {
        return {
          limit: 0,
          isBulkAdjustment: true,
          bulkAdjustmentType: this.inventoryItemBulkAdjustment.adjustmentType,
          locationId: this.inventoryItemBulkAdjustment.location?.id,
          registerStationId: this.inventoryItemBulkAdjustment.registerStation?.id,
          transferLocationId: this.inventoryItemBulkAdjustment.transferLocationId,
          transferRegisterStationId: this.inventoryItemBulkAdjustment.transferRegisterStationId,
          effectiveAt: this.inventoryItemBulkAdjustment.effectiveAt,
          force: false,
          skip: !this.shown || !this.inventoryItemBulkAdjustment.showAdjustmentsTable || this.inventoryItemBulkAdjustment.isCommitted
        }
      },
      force: true
    },
    inventoryRecipes: {
      inventoryRecipeItems: true,
      variables () {
        return {
          limit: 0,
          skip: !this.shown || !this.inventoryItemBulkAdjustment.showAdjustmentsTable || !this.inventoryItemBulkAdjustment.isWaste
        }
      }
    },
    inventorySuppliers: {
      variables () {
        return {
          limit: 0,
          skip: !this.shown
        }
      }
    },
    inventoryItemBalancesByLocationAndRegisterStation: {
      variables () {
        return {
          effectiveAt: this.inventoryItemBulkAdjustment.effectiveAt,
          locationId: this.inventoryItemBulkAdjustment.location?.id,
          registerStationId: this.inventoryItemBulkAdjustment.registerStation?.id,
          force: false,
          skip: (
            !this.shown ||
            !this.inventoryItemBulkAdjustment.showAdjustmentsTable ||
            !this.inventoryItemBulkAdjustment.isStocktake ||
            this.inventoryItemBulkAdjustment.isCommitted
          )
        }
      }
    }
  },
  methods: {
    async downloadPdf () {
      this.pdfProcessing = true
      await this.$nextTick()
      const element = document.getElementById('inventory-item-adjustments-preview')
      const pdfOptions = {
        margin: 10,
        filename: `Stocktake - ${this.inventoryItemBulkAdjustment.location?.name} (${this.currentSite.name}) - ${this.currentSite.businessDate}.pdf`,
        pagebreak: { avoid: 'tr' }
      }
      html2pdf()
        .set(pdfOptions)
        .from(element)
        .outputPdf()
        .get('pdf')
        .then(function (pdf) {
          const pdfUrl = pdf.output('bloburl')
          window.open(pdfUrl, '_blank')
        })

      this.pdfProcessing = false
    },
    show (model, initialData) {
      this.$refs.modelForm.show(model, initialData)
    },
    effectiveDateOptionsFn (date) {
      const dateOption = new Date(date)
      const { startDate, endDate } = this.currentSite.activeInventoryStrategy
      const isAfterStartDate = !startDate || dateOption >= startDate
      const isBeforeEndDate = !endDate || dateOption <= endDate
      return this.currentUser?.superUser || (isAfterStartDate && isBeforeEndDate)
    },
    refreshInventoryItemBalances () {
      this.inventoryItemBalancesByLocationAndRegisterStation.options.variables.force = true
      this.inventoryItemBalancesByLocationAndRegisterStation.fetch(true)
      this.inventoryItemBalancesByLocationAndRegisterStation.options.variables.force = false
    },
    getInventoryItemName (inventoryItemAdjustment) {
      return inventoryItemAdjustment.inventoryItem?.name
    },
    getInventoryItemUnitDescription (inventoryItemAdjustment) {
      return inventoryItemAdjustment.inventoryItemUnit?.description
    },
    getItemClassName (inventoryItemAdjustment) {
      const inventoryItem = inventoryItemAdjustment.inventoryItem

      if (!inventoryItem) return this.itemClassName

      return inventoryItem.itemClass?.name
    },
    getInventoryItemTotalBaseQuantity (inventoryItemAdjustment) {
      const inventoryItemId = inventoryItemAdjustment.inventoryItem?.id

      if (!inventoryItemId) return 0

      return _(this.inventoryItemBulkAdjustment.inventoryItemAdjustments.data)
        .filter(adjustment => adjustment.inventoryItemId === inventoryItemId && adjustment.inventoryItemUnit && adjustment.unitQuantity)
        .map(adjustment => adjustment.baseQuantity || (adjustment.inventoryItemUnit.baseQuantity * adjustment.unitQuantity))
        .sum()
    },
    getBalancesForInventoryItemFromBalancesQuery (inventoryItem) {
      return this.inventoryItemBalancesByLocationAndRegisterStation?.data?.value?.[inventoryItem?.id]
    },
    getInventoryItemBalance (inventoryItemAdjustment) {
      if (!inventoryItemAdjustment.inventoryItem || inventoryItemAdjustment.isCommitted) return null

      const inventoryItem = this.inventoryItems.data.find(inventoryItem => inventoryItem.id === inventoryItemAdjustment.inventoryItem.id)
      if (!inventoryItem) return 0

      let balanceForLocationOrRegisterStation

      if (this.inventoryItemBulkAdjustment.location) {
        balanceForLocationOrRegisterStation = this.getBalancesForInventoryItemFromBalancesQuery(inventoryItem)?.locations?.[this.inventoryItemBulkAdjustment.location.id]
      } else if (this.inventoryItemBulkAdjustment.registerStation) {
        balanceForLocationOrRegisterStation = this.getBalancesForInventoryItemFromBalancesQuery(inventoryItem)?.register_stations?.[this.inventoryItemBulkAdjustment.registerStation.id]
      }

      return balanceForLocationOrRegisterStation || 0
    },
    getInventoryItemCountDiff (inventoryItemAdjustment) {
      if (inventoryItemAdjustment.isCommitted) return null

      const balance = this.getInventoryItemBalance(inventoryItemAdjustment)

      if (balance === null) return

      return (this.getInventoryItemTotalBaseQuantity(inventoryItemAdjustment)) - balance
    },
    onProgress (percentageComplete) {
      this.pdfLoading = percentageComplete !== 100
    },
    generateAdjustments () {
      _.sortBy(this.inventoryItemOptions, 'itemClass.name').forEach(inventoryItem => {
        let balanceForLocationOrRegisterStation

        if (this.inventoryItemBulkAdjustment.location) {
          balanceForLocationOrRegisterStation = this.getBalancesForInventoryItemFromBalancesQuery(inventoryItem)?.locations?.[this.inventoryItemBulkAdjustment.location.id]
        } else if (this.inventoryItemBulkAdjustment.registerStation) {
          balanceForLocationOrRegisterStation = this.getBalancesForInventoryItemFromBalancesQuery(inventoryItem)?.register_stations?.[this.inventoryItemBulkAdjustment.registerStation.id]
        }

        const adjustmentForInventoryItem = _.find(this.inventoryItemBulkAdjustment.inventoryItemAdjustments.data, adj => adj.inventoryItem?.id === inventoryItem.id)

        /**
         * Never generate an adjustment if the inventory item already has one.
         * Otherwise, only generate an adjustment if we expect the balance to be non-zero.
         */

        if (
          !adjustmentForInventoryItem && (
            (balanceForLocationOrRegisterStation && balanceForLocationOrRegisterStation !== 0)
          )
        ) {
          const inventoryItemUnit = inventoryItem.baseInventoryItemUnit
          this.inventoryItemBulkAdjustment.inventoryItemAdjustments.add({
            ...this.inventoryItemBulkAdjustment.constructorData,
            inventoryItemId: inventoryItem.id,
            inventoryItem,
            inventoryItemUnit
          })
        }
      })
    },
    inventoryItemFilterFunction (val, update) {
      update(() => {
        this.inventoryItemsFilterString = val.length ? val.trim() : null
      })
    },
    inventoryRecipeFilterFunction (val, update) {
      update(() => {
        this.inventoryRecipesFilterString = val.length ? val.trim() : null
      })
    },
    inventorySupplierFilterFunction (val, update) {
      update(() => {
        this.inventorySuppliersFilterString = val.length ? val.trim() : null
      })
    },
    validateAdjustmentType () {
      if (this.inventoryItemBulkAdjustment.isStocktake) {
        this.inventoryItemBulkAdjustment.inventorySupplier = null
        this.inventoryItemBulkAdjustment.purchaseOrder = null
      }

      if (this.inventoryItemBulkAdjustment.isReceipt) {
        this.itemClassId = null
      }
    },
    generateAdjustmentsFromRecipe () {
      _(this.inventoryRecipeToWaste.inventoryRecipeItems.data).each(inventoryRecipeItem => {
        const inventoryItem = _.find(this.inventoryItems.data, inventoryItem => inventoryItem.id === inventoryRecipeItem.inventoryItemId)
        const inventoryItemUnit = inventoryItem.baseInventoryItemUnit
        const unitQuantity = inventoryRecipeItem.baseQuantity * this.inventoryRecipeToWasteQuantity
        this.inventoryItemBulkAdjustment.inventoryItemAdjustments.add({
          ...this.inventoryItemBulkAdjustment.constructorData,
          inventoryItemId: inventoryItem.id,
          inventoryItem,
          inventoryItemUnit,
          unitQuantity
        })
      })

      this.inventoryRecipeToWaste = null
      this.inventoryRecipeToWasteQuantity = 1
      this.showRecipeWastageAdjustmentDialog = false
    },
    copyInventoryItemAdjustment (adjustment) {
      if (!adjustment.inventoryItem || !adjustment.inventoryItemUnit) return

      this.inventoryItemBulkAdjustment.inventoryItemAdjustments.add({
        ...this.inventoryItemBulkAdjustment.constructorData,
        inventoryItemId: adjustment.inventoryItem.id,
        inventoryItem: adjustment.inventoryItem,
        inventoryItemUnit: adjustment.inventoryItemUnit
      })
    }
  }
}
</script>

<style>
.pdf-source-container {
  position: fixed;
  width: 100vw;
  height: 100vh;
  left: -100vw;
  top: 0;
  z-index: -9999;
  background: rgba(95, 95, 95, 0.8);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  overflow: auto;
}
</style>
